import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import EditableText from "./EditableText";
import { RowActions, AddRowButton } from "./RowActions";
import { useReorder } from "../lib/useReorder";
import { useSwipeToDelete } from "../lib/useSwipeToDelete";
import { useEditMode } from "../lib/EditModeContext";
import SourceBadge from "./SourceBadge";
import { toast } from "sonner";

const notifyTodoist = (res) => {
  if (!res?._todoist) return;
  if (res._todoist === "created") toast("Created in Todoist");
  else if (res._todoist === "updated") toast("Updated in Todoist");
  else if (res._todoist === "closed") toast("Closed in Todoist");
  else if (res._todoist === "reopened") toast("Reopened in Todoist");
  else if (res._todoist === "sync_failed") toast("Todoist sync failed", { description: "Local change kept." });
};

const undoDelete = (snapshot, restoreFn) => (
  toast("Row deleted", {
    action: {
      label: "UNDO",
      onClick: async () => {
        try {
          const { id: _, todoist_task_id: __, todoist_url: ___, ...body } = snapshot;
          await restoreFn(body);
          toast("Restored");
        } catch { toast("Undo failed"); }
      },
    },
    duration: 6000,
  })
);

const DndRow = ({ i, rowProps, handleProps, dragId, overId, load, remove, toggle, notifyTodoist }) => {
  const { swipeProps, swipeStyle, revealPct } = useSwipeToDelete(() => remove(i));
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-end pr-6 bg-[rgba(255,90,90,0.14)] text-[#FF7A7A] op-mono text-[10px] tracking-[0.24em] pointer-events-none" style={{ opacity: revealPct }}>
        DELETE
      </div>
      <div
        data-testid={`dnd-${i.id}`}
        {...rowProps(i.id)}
        {...swipeProps}
        style={swipeStyle}
        data-dragging={dragId === i.id}
        data-drop-active={overId === i.id}
        className={`op-editable-row grid grid-cols-12 gap-4 items-center px-6 py-4 bg-[var(--op-panel-bg,rgba(10,12,20,1))] ${i.completed ? "opacity-40" : ""}`}
      >
        <div className="col-span-12 md:col-span-5 flex items-center gap-3">
          <button
            onClick={() => toggle(i)}
            data-testid={`dnd-check-${i.id}`}
            className="op-check"
            data-checked={i.completed}
            aria-label="toggle"
          />
          <EditableText
            value={i.label}
            onSave={async (v) => { const res = await api.patchDND(i.id, { label: v }); notifyTodoist(res); load(); }}
            className={`text-white text-sm flex-1 ${i.completed ? "line-through" : ""}`}
            multiline
            testid={`dnd-label-${i.id}`}
            emptyPlaceholder="Untitled commitment…"
          />
          <SourceBadge source={i.source} />
        </div>
        <div className="col-span-4 md:col-span-2 op-mono text-xs text-[#A0A5B5]">
          <EditableText
            value={i.due || ""}
            onSave={async (v) => { await api.patchDND(i.id, { due: v }); load(); }}
            emptyPlaceholder="—"
            testid={`dnd-due-${i.id}`}
          />
        </div>
        <div className="col-span-5 md:col-span-3 text-xs text-[#A0A5B5]">
          <EditableText
            value={i.follow_up || ""}
            onSave={async (v) => { await api.patchDND(i.id, { follow_up: v }); load(); }}
            emptyPlaceholder="—"
            testid={`dnd-followup-${i.id}`}
          />
        </div>
        <div className="col-span-3 md:col-span-2 flex md:justify-end items-center gap-2">
          <RowActions onDelete={() => remove(i)} testid={`dnd-${i.id}`} handleProps={handleProps(i.id)} />
          <span className="op-tag text-[#00E5FF]">{i.completed ? "CLOSED" : "OPEN"}</span>
        </div>
      </div>
    </div>
  );
};

const DoNotDrop = () => {
  const [items, setItems] = useState([]);
  const { editMode } = useEditMode();
  const load = () => api.doNotDrop().then(setItems);
  useEffect(() => { load(); }, []);

  const toggle = async (i) => {
    const res = await api.patchDND(i.id, { completed: !i.completed });
    notifyTodoist(res);
    load();
  };
  const remove = async (i) => {
    setItems((prev) => prev.filter((x) => x.id !== i.id));
    try {
      await api.deleteDND(i.id);
      undoDelete(i, async (body) => {
        const restored = await api.createDND(body);
        setItems((prev) => [...prev, restored]);
      });
    } catch { load(); }
  };
  const add = async () => {
    const created = await api.createDND({ label: "" });
    setItems((prev) => [...prev, created]);
  };
  const { rowProps, handleProps, dragId, overId } = useReorder(items, async (newIds) => {
    setItems((prev) => newIds.map((id) => prev.find((p) => p.id === id)).filter(Boolean));
    try { await api.reorderDND(newIds); } catch { load(); }
  });

  return (
    <section data-testid="dnd-section">
      <SectionHeader
        code="03" label="DO NOT DROP"
        caption="Commitments that cannot disappear."
        labelKey="section.dnd.label"
        captionKey="section.dnd.caption"
      />
      <div className="op-panel">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/[0.05]">
          <div className="col-span-5 op-tag">ITEM</div>
          <div className="col-span-2 op-tag">DUE</div>
          <div className="col-span-3 op-tag">FOLLOW-UP</div>
          <div className="col-span-2 op-tag text-right">STATUS</div>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {items.map((i) => (
            <DndRow
              key={i.id}
              i={i}
              rowProps={rowProps}
              handleProps={handleProps}
              dragId={dragId}
              overId={overId}
              load={load}
              remove={remove}
              toggle={toggle}
              notifyTodoist={notifyTodoist}
            />
          ))}
        </div>
        {editMode && (
          <div className="px-6 py-4 border-t border-white/[0.05]">
            <AddRowButton onClick={add} label="ADD COMMITMENT" testid="dnd-add" />
          </div>
        )}
      </div>
    </section>
  );
};

export default DoNotDrop;
